import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";

import * as ConnectionResolver from "./resolver.ts";
import * as ConnectionDriver from "./driver.ts";
import * as EnvironmentRegistry from "./registry.ts";
import * as PlatformConnectionSource from "../platform/source.ts";
import * as RelayEnvironmentDiscovery from "../relay/discovery.ts";
import * as RemoteEnvironmentAuthorization from "../authorization/service.ts";
import * as RpcSession from "../rpc/session.ts";
import { RelayConnectionRegistration } from "./catalog.ts";
import { RelayConnectionTarget } from "./model.ts";

const resolverLayer = ConnectionResolver.layer.pipe(
  Layer.provide(RemoteEnvironmentAuthorization.layer),
);

const driverLayer = ConnectionDriver.layer.pipe(
  Layer.provide(Layer.mergeAll(resolverLayer, RpcSession.layer)),
);

const registryLayer = EnvironmentRegistry.layer.pipe(Layer.provide(driverLayer));

const connectionServicesLayer = Layer.mergeAll(registryLayer, RelayEnvironmentDiscovery.layer);

const connectionStartupLayer = Layer.effectDiscard(
  Effect.gen(function* () {
    const registry = yield* EnvironmentRegistry.EnvironmentRegistry;
    const platformSource = yield* PlatformConnectionSource.PlatformConnectionSource;
    const relayDiscovery = yield* RelayEnvironmentDiscovery.RelayEnvironmentDiscovery;
    yield* registry.start;
    yield* platformSource.registrations.pipe(
      Stream.runForEach(registry.reconcilePlatform),
      Effect.forkScoped,
    );
    yield* SubscriptionRef.changes(relayDiscovery.state).pipe(
      Stream.filter(
        (state) => state.sessionResolved && !state.refreshing && Option.isNone(state.error),
      ),
      Stream.map((state) =>
        [...state.environments.values()].map(
          ({ environment }) =>
            new RelayConnectionRegistration({
              target: new RelayConnectionTarget({
                environmentId: environment.environmentId,
                label: environment.label,
                ...(state.accountKey === undefined ? {} : { accountKey: state.accountKey }),
              }),
            }),
        ),
      ),
      Stream.runForEach(registry.reconcileRelay),
      Effect.forkScoped,
    );
  }).pipe(Effect.withSpan("clientRuntime.connection.application.start")),
);

export const layer = connectionStartupLayer.pipe(Layer.provideMerge(connectionServicesLayer));
